import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Breadcrumbs, Container, Section, SectionHeader,
} from "../components/Primitives";
import { VideoBand } from "../components/VideoBand";
import { Ornament } from "../components/Ornament";
import { GrahaQuickNav, RashiQuickNav } from "../components/QuickNav";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { LineReveal, Reveal } from "../lib/motion";

const CONFIG = {
  planet: {
    band: {
      src: "/video/navgraha-cosmos.mp4",
      webm: "/video/navgraha-cosmos.webm",
      poster: "/video/navgraha-cosmos-poster.jpg",
      scrim: 0.4,
    },
    eyebrow: "Browse by graha",
    h1: "Shop by Planet — The Nine Grahas and Their Stones",
    lede: "Each graha carries one principal stone in the classical scheme. Pick a graha to see what we currently hold, with the laboratory report on every listing.",
    title: "Shop Gemstones by Planet — Navgraha Stones, Lab Verified",
    crumb: "By planet",
  },
  rashi: {
    band: {
      src: "/video/rashi-cosmic-ocean.mp4",
      webm: "/video/rashi-cosmic-ocean.webm",
      poster: "/video/rashi-cosmic-ocean-poster.jpg",
      scrim: 0.64,
    },
    eyebrow: "Browse by rashi",
    h1: "Shop by Rashi — Twelve Signs, Twelve Starting Points",
    lede: "A chart is more than a sun sign. These routes list the stones classically associated with each sign's ruling graha — a starting point for a reading, not a recommendation.",
    title: "Shop Gemstones by Rashi — Certified, Verifiable Stones",
    crumb: "By rashi",
  },
};

export default function BrowseIndex() {
  const { pathname } = useLocation();
  const kind = pathname.includes("by-rashi") ? "rashi" : "planet";
  const cfg = CONFIG[kind];

  useSEO({
    title: cfg.title,
    description: cfg.lede,
    canonical: pathname,
    jsonLd: breadcrumbSchema([
      { label: "Home", to: "/" }, { label: "Shop", to: "/shop" }, { label: cfg.crumb },
    ]),
  });

  return (
    <>
      <VideoBand
        src={cfg.band.src}
        webm={cfg.band.webm}
        poster={cfg.band.poster}
        scrim={cfg.band.scrim}
        surround="#faf7f1"
        testId={`browse-band-${kind}`}
      >
        <Container className="py-[110px] text-center max-md:py-[70px]">
          <Reveal>
            <Ornament className="mb-7" />
            <p className="eyebrow-wide">{cfg.eyebrow}</p>
            <h1 className="hero-h1 mx-auto mt-6 max-w-[var(--measure-tight)] !text-[clamp(28px,4.5vw,52px)] !text-[#f3efe7]">
              <LineReveal lines={cfg.h1.split(" — ")} inViewAmount={0.4} stagger={0.12} duration={0.9} />
            </h1>
            <p className="hero-desc ui mx-auto mt-7 text-[#f3efe7]/85">{cfg.lede}</p>
            <Link to="/consult/suitability-session" className="btn btn-primary btn-cta mt-9" data-testid={`browse-cta-${kind}`}>
              Not sure? Get a straight answer — ₹499
            </Link>
          </Reveal>
        </Container>
      </VideoBand>

      <Container className="pt-8">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }, { label: cfg.crumb }]} />
      </Container>

      <Section className="!py-[90px] max-md:!py-[60px]">
        <Container>
          <SectionHeader
            eyebrow={kind === "planet" ? "Nine grahas" : "Twelve signs"}
            title={kind === "planet"
              ? "Pick a graha to see what we currently hold"
              : "Pick a sign to see what we currently hold"}
          />
          {kind === "planet" ? <GrahaQuickNav /> : <RashiQuickNav />}
        </Container>
      </Section>
    </>
  );
}
